import { Badge, Button } from '@/components/UI';
import ObjectiveModeControl, { type ObjectiveMode } from '../parameterDiscovery/ObjectiveModeControl';
import ScorecardMetricControl from './ScorecardMetricControl';
import SummaryStat from './SummaryStat';
import type { ParameterDiscoveryMetric, ScorecardDraft } from './types';

interface ScorecardDraftSectionProps {
    draft: ScorecardDraft | undefined;
    experimentId: number;
    duration: string;
    horizonSeconds: number;
    scorecardMetric: ParameterDiscoveryMetric;
    objectiveMode: ObjectiveMode;
    objectiveEvidenceReady: boolean;
    realizedOutcomeSetId: number | null;
    isLoading: boolean;
    errorMessage: string | undefined;
    actionPending: boolean;
    isCreating: boolean;
    createdScorecardLink: { scorecardId: number; strategyId: number } | null;
    hasOptionMetricData: boolean | undefined;
    onObjectiveModeChange: (mode: ObjectiveMode) => void;
    onRealizedOutcomeSetIdChange: (id: number | null) => void;
    onScorecardMetricChange: (value: ParameterDiscoveryMetric) => void;
    onCreateScorecard: () => void;
}

const ScorecardDraftSection = ({
    draft,
    experimentId,
    duration,
    horizonSeconds,
    scorecardMetric,
    objectiveMode,
    objectiveEvidenceReady,
    realizedOutcomeSetId,
    isLoading,
    errorMessage,
    actionPending,
    isCreating,
    createdScorecardLink,
    hasOptionMetricData,
    onObjectiveModeChange,
    onRealizedOutcomeSetIdChange,
    onScorecardMetricChange,
    onCreateScorecard
}: ScorecardDraftSectionProps) => {
    const realizedOutcomeSetMissing = objectiveMode === 'strategy_exit' && realizedOutcomeSetId === null;

    return (
        <div className="rounded border border-zinc-800 bg-zinc-950/40 p-3">
            <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                    <div className="font-medium text-white">Scorecard Draft</div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                        <span>{scorecardMetric === 'options' ? 'Options' : 'Stock'} authoring</span>
                        <Badge variant={objectiveMode === 'strategy_exit' ? 'success' : 'secondary'}>
                            {objectiveMode === 'strategy_exit' ? `realized #${realizedOutcomeSetId ?? 'none'}` : 'fixed horizon'}
                        </Badge>
                    </div>
                </div>
                <div className="flex flex-col gap-2 md:items-end">
                    {draft && (
                        <Badge variant={draft.writableRuleCount > 0 ? 'success' : 'secondary'}>{draft.writableRuleCount} writable</Badge>
                    )}
                    <ScorecardMetricControl
                        disabled={actionPending}
                        hasOptionMetricData={hasOptionMetricData}
                        value={scorecardMetric}
                        onChange={onScorecardMetricChange}
                    />
                </div>
            </div>
            <div className="mb-4">
                <ObjectiveModeControl
                    requireOutcomeSet
                    disabled={actionPending}
                    duration={duration}
                    experimentId={experimentId}
                    horizonSeconds={horizonSeconds}
                    objectiveMode={objectiveMode}
                    realizedOutcomeSetId={realizedOutcomeSetId}
                    selectId="scorecard-draft-realized-outcome-set"
                    onObjectiveModeChange={onObjectiveModeChange}
                    onRealizedOutcomeSetIdChange={onRealizedOutcomeSetIdChange}
                />
            </div>
            {isLoading ? (
                <div className="text-sm text-zinc-500">Building draft...</div>
            ) : realizedOutcomeSetMissing ? (
                <div className="text-sm text-amber-300">Select a ready realized outcome set.</div>
            ) : !objectiveEvidenceReady ? (
                <div className="text-sm text-amber-300">Prepare realized parameter discovery before building a scorecard draft.</div>
            ) : errorMessage ? (
                <div className="text-sm text-red-300">{errorMessage}</div>
            ) : draft ? (
                <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                        <SummaryStat label="Candidates" value={draft.candidateCount} />
                        <SummaryStat label="Rules" value={draft.rules.length} />
                        <SummaryStat label="Skipped" value={draft.skippedRuleCount} />
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Button
                            className="inline-flex items-center"
                            disabled={
                                actionPending || !objectiveEvidenceReady || realizedOutcomeSetMissing || draft.writableRuleCount === 0
                            }
                            isLoading={isCreating}
                            size="sm"
                            variant="primary"
                            onClick={onCreateScorecard}
                        >
                            Create {scorecardMetric === 'options' ? 'Options' : 'Stock'} Scorecard
                        </Button>
                        {createdScorecardLink && (
                            <Button
                                size="sm"
                                variant="secondary"
                                onClick={() =>
                                    window.open(
                                        `/user/scorecard/${createdScorecardLink.scorecardId}?scope=strategy&strategyId=${createdScorecardLink.strategyId}`,
                                        '_blank'
                                    )
                                }
                            >
                                Open Created
                            </Button>
                        )}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default ScorecardDraftSection;
